import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { OvertimeApprovalTable } from "@/components/supervisor/overtime/OvertimeApprovalTable";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { useFetchOvertimeApprovals } from "@/hooks/api/overtime/useFetchOvertimeApprovals";
import { normalizeOvertimeApproval } from "@/hooks/api/overtime/normalizeOvertimeApproval";
import { APPROVAL_STATUS_LABELS, type ApprovalStatus } from "@/types/approval";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import { requireFeature, requireSetting } from "@/lib/feature-gate";

export const Route = createFileRoute("/supervisor/_layout/overtime-requests")({
  beforeLoad: () => {
    requireFeature("attendance");
    requireSetting("overtime_approval_required");
  },
  component: OvertimeRequestsPage,
});

const STATUSES: ApprovalStatus[] = [
  "PENDING",
  "APPROVED",
  "REJECTED",
  "CANCELLED",
];

function OvertimeRequestsPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<"ALL" | ApprovalStatus>("ALL");
  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();
  const [showFilters, setShowFilters] = useState(false);

  const requestsQuery = useFetchOvertimeApprovals({
    page,
    page_size: pageSize,
    is_supervisor: true,
    // axios drops undefined params, so "ALL" means no status filter.
    status: status === "ALL" ? undefined : status,
  });
  const requests = useMemo(
    () =>
      (requestsQuery.data?.results ?? []).map(
        (raw) => normalizeOvertimeApproval(raw).request
      ),
    [requestsQuery.data]
  );
  const count = requestsQuery.data?.count ?? 0;
  const loading = requestsQuery.isPending;
  const isFetching = requestsQuery.isFetching;

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="All Overtime Requests"
          description="Overtime approval history for requests routed to you."
          onToggleFilter={() => setShowFilters((p) => !p)}
        />

        <FiltersPanel
          show={showFilters}
          onClear={() => {
            setStatus("ALL");
            setPage(1);
          }}
        >
          <Select
            value={status}
            onValueChange={(v) => {
              // Resolve the Radix string back to a known status; the only
              // non-status option rendered is "ALL".
              setStatus(STATUSES.find((s) => s === v) ?? "ALL");
              setPage(1);
            }}
          >
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All statuses</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {APPROVAL_STATUS_LABELS[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FiltersPanel>

        <OvertimeApprovalTable
          requests={requests}
          loading={loading}
          onViewDetails={(request) =>
            navigate({
              to: "/supervisor/overtime-requests/$id",
              params: { id: String(request.id) },
            })
          }
          emptyMessage="No overtime requests match this filter."
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={setPage}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
      </div>
    </SidebarInset>
  );
}
